import { useEffect, useRef, useState } from 'react'

/**
 * useState que persiste su valor en localStorage.
 *
 * Se comporta igual que useState, pero lee el valor inicial desde
 * localStorage (si existe) y vuelve a guardarlo cada vez que cambia. Pensado
 * para preferencias de UI del usuario (vista activa, filtros, etc.).
 *
 * @template T
 * @param {string} key - Clave bajo la que se guarda en localStorage.
 * @param {T} defaultValue - Valor por defecto si no hay nada guardado.
 * @returns {[T, React.Dispatch<React.SetStateAction<T>>]}
 */
export default function usePersistedState(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      const stored = window.localStorage.getItem(key)
      return stored !== null ? JSON.parse(stored) : defaultValue
    } catch {
      // localStorage no disponible o JSON corrupto: usamos el default.
      return defaultValue
    }
  })

  // Evitamos escribir en el primer render: el valor ya viene de localStorage.
  const isFirst = useRef(true)

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false
      return
    }
    try {
      window.localStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Si no se puede persistir (modo privado, cuota), seguimos sin romper.
    }
  }, [key, value])

  return [value, setValue]
}
